/**
 * @file VariantObjField.js
 * @description
 *
 * @copyright veriome labs, llc.  - all rights reserved.
 * This file is subject to the terms and conditions defined in
 * file 'LICENSE.md', which is part of this source code package.
 * 
 * @author don michael
 * @since Mar 24, 2022 11:59
 */
import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';

import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardActions from '@mui/material/CardActions';
import CardContent from '@mui/material/CardContent';
import FormLabel from '@mui/material/FormLabel';
import Grid from '@mui/material/Grid';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';

import { arrayExists } from 'src/@common/utilities';
import { findLabel, findOptions, findHelpText } from 'src/@common/schemaUtilities';
import GDotObjField from 'src/components/fieldComponents/GDotObjField/GDotObjFieldContainer';
import PDotObjField from 'src/components/fieldComponents/PDotObjField/PDotObjFieldContainer';
import GDotLookupButtonContainer from 'src/components/fieldComponents/GDotLookupButton/GDotLookupButtonContainer';
import GeneSymbolField from 'src/components/fieldComponents//GeneSymbolField';
import VeriomeAutocompleteField from 'src/components/fieldComponents/VeriomeAutocompleteField';
import VeriomeSelectField from 'src/components/fieldComponents/VeriomeSelectField';
import VeriomeTextField from 'src/components/fieldComponents/VeriomeTextField';

function VariantObjField(props) {
    const {
        annotationType,
        biocurations,
        fieldSchema,
        formFields,
        isArrayElement,
        handleBlur,
        handleChange,
        handleDelete,
        handleLookup,
    } = props;

    const [gDotVariants, setGDotVariants] = useState([]);
    const [cDotVariants, setCDotVariants] = useState([]);
    const [pDotVariants, setPDotVariants] = useState([]);

    const fieldErrors = {};

    useEffect(() => {

        if (arrayExists(biocurations.variants)) {
            const gDot = [];
            const cDot = [];
            const pDot = [];

            biocurations.variants.forEach((variant) => {

                if (variant.includes('c.')) {
                    cDot.push(variant);
                } else if (variant.includes('g.')) {
                    gDot.push(variant);
                } else if (variant.includes('p.')) {
                    pDot.push(variant);
                }

                cDot.sort((a, b) => (a > b) ? 1 : -1);
                gDot.sort((a, b) => (a > b) ? 1 : -1);
                pDot.sort((a, b) => (a > b) ? 1 : -1);

                setGDotVariants(gDot);
                setCDotVariants(cDot);
                setPDotVariants(pDot);
            });
        }

    }, [biocurations]);

    return (

        <Card variant="outlined" className="shadow-1 mt-10 mb-10 ml-0 mr-5" >

            <CardContent>
                {/* <FormLabel className={classes.formLabel} component="legend">Variant</FormLabel> */}
                <FormLabel
                    sx={{
                        mt: '3px',
                        mb: '5px'
                    }}
                    component="legend">
                    Variant
                </FormLabel>

                {annotationType === 'generalFeature' &&
                    <Grid container
                        spacing={1}
                        direction="row"
                        justifyContent="flex-start"
                        alignItems="flex-start"
                    >
                        <GeneSymbolField
                            currentValue={formFields.geneSymbol ? formFields.geneSymbol : ''}
                            fieldLabel={findLabel(fieldSchema, 'geneSymbol')}
                            helpText={findHelpText(fieldSchema, 'geneSymbol')}
                            isFocus={true}
                            handleBlur={() => {
                                handleBlur('geneSymbol');
                            }}
                            handleChange={(event) => {
                                handleChange(event, 'geneSymbol');
                            }}
                        />
                    </Grid>
                }

                <Grid container
                    spacing={1}
                    direction="row"
                    justifyContent="flex-start"
                    alignItems="flex-start"
                >
                    <Grid size={{ xs: 3, sm: 3 }}>
                        <VeriomeTextField
                            fieldErrors={fieldErrors}
                            fieldLabel={findLabel(fieldSchema, 'chromosome')}
                            fieldName={'chromosome'}
                            currentValue={formFields.chromosome ? formFields.chromosome : ''}
                            helpText={findHelpText(fieldSchema, 'chromosome')}
                            isFocus={true}
                            isRequired={false}
                            isMultiline={false}
                            sxStyle={{
                                width: 115,
                                ml: '-2px',
                            }}
                            handleBlur={() => {
                                handleBlur('chromosome');
                            }}
                            handleChange={(event) => {
                                handleChange(event, 'chromosome');
                            }}
                        />

                    </Grid>


                    <Grid size={{ sm: 3, xs: 3 }} >

                        <VeriomeSelectField
                            className={''}
                            fieldLabel={findLabel(fieldSchema, 'genomeBuild')}
                            fieldName={'genomeBuild'}
                            currentValue={formFields.genomeBuild ? formFields.genomeBuild : ''}
                            options={findOptions(fieldSchema, 'genomeBuild')}
                            fieldErrors={fieldErrors}
                            helpText={findHelpText(fieldSchema, 'genomeBuild')}
                            isFocus={false}
                            isRequired={false}
                            sxStyle={{
                                width: 120,
                                ml: '1px',
                                mr: '15px'
                            }}
                            handleBlur={() => {
                                handleBlur('genomeBuild');
                            }}
                            handleChange={(event) => {
                                handleChange(event, 'genomeBuild');
                            }}
                        />

                    </Grid>

                    <Grid size={{ sm: 4, xs: 4 }} >

                        <VeriomeAutocompleteField
                            currentValue={formFields.transcript ? formFields.transcript : ''}
                            fieldErrors={fieldErrors}
                            fieldName={'transcript'}
                            fieldLabel={findLabel(fieldSchema, 'transcript')}
                            helpText={findHelpText(fieldSchema, 'transcript')}
                            options={biocurations.transcripts}
                            sxStyle={{
                                width: 200,
                                ml: '10px',
                                mr: '10px'
                            }}
                            handleBlur={() => {
                                handleBlur('transcript');
                            }}
                            handleChange={(event) => {
                                handleChange(event, 'transcript');
                            }}
                        />

                    </Grid>

                    <Stack
                        direction="row"
                        justifyContent="flex-start"
                        alignItems="center"
                        spacing={1}
                    >
                        <VeriomeAutocompleteField
                            currentValue={formFields.cDot ? formFields.cDot : ''}
                            fieldErrors={fieldErrors}
                            fieldName={'cDot'}
                            fieldLabel={findLabel(fieldSchema, 'cDot')}
                            helpText={findHelpText(fieldSchema, 'cDot')}
                            options={cDotVariants}
                            sxStyle={{
                                width: 200,
                                mb: '5px',
                                mr: '10px'
                            }}
                            handleBlur={() => {
                                handleBlur('cDot');
                            }}
                            handleChange={(event) => {
                                handleChange(event, 'cDot');
                            }}
                        />

                        <GDotLookupButtonContainer
                            gDotObj={formFields.gDotObj}
                            cDot={formFields.cDot}
                            transcript={formFields.transcript}
                            handleGDotLookup={handleLookup}
                        />

                    </Stack>

                    <Grid container
                        spacing={1}
                        direction="column"
                        justifyContent="flex-start"
                        alignItems="flex-start"
                    >
                        <Grid  >
                            <GDotObjField
                                gDotOptions={gDotVariants}
                                containerSxStyle={{ maxWidth: '300px', ml: '-1px', mr: '8px', mt: '5px' }}
                                gDotObj={formFields.gDotObj}
                                handleBlur={(value) => {
                                    handleBlur('gDotObj', value);
                                }}
                            />
                        </Grid>

                    </Grid>

                    <Grid container
                        spacing={1}
                        direction="column"
                        justifyContent="flex-start"
                        alignItems="flex-start"
                    >
                        <Grid  >
                            <PDotObjField
                                pDotOptions={pDotVariants}
                                containerSxStyle={{ maxWidth: '300px', ml: '-1px', mr: '8px', mt: '5px' }}
                                pDotObj={formFields.pDotObj}
                                handleBlur={(value) => {
                                    handleBlur('pDotObj', value);
                                }}
                            />
                        </Grid>

                    </Grid>

                    <Grid container
                        spacing={1}
                        direction="row"
                        justifyContent="flex-start"
                        alignItems="flex-start"
                    >
                        <Grid size={{ xs: 5, sm: 5 }} >
                            <VeriomeAutocompleteField
                                currentValue={formFields.dnaContig ? formFields.dnaContig : ''}
                                fieldErrors={fieldErrors}
                                fieldName={'dnaContig'}
                                fieldLabel={findLabel(fieldSchema, 'dnaContig')}
                                helpText={findHelpText(fieldSchema, 'dnaContig')}
                                options={biocurations.dnaContigs}
                                sxStyle={{
                                    width: 185,
                                    mr: '10px',
                                }}
                                handleBlur={() => {
                                    handleBlur('dnaContig');
                                }}
                                handleChange={(event) => {
                                    handleChange(event, 'dnaContig');
                                }}
                            />
                        </Grid>

                        <Grid size={{ xs: 5, sm: 5 }} >
                            <VeriomeTextField
                                fieldErrors={fieldErrors}
                                fieldLabel={findLabel(fieldSchema, 'cnv')}
                                fieldName={'cnv'}
                                currentValue={formFields.cnv ? formFields.cnv : ''}
                                helpText={findHelpText(fieldSchema, 'cnv')}
                                isFocus={false}
                                isRequired={false}
                                isMultiline={false}
                                sxStyle={{
                                    width: 200,
                                    mr: '5px',
                                    ml: '-5px',
                                }}
                                handleBlur={() => {
                                    handleBlur('cnv');
                                }}
                                handleChange={(event) => {
                                    handleChange(event, 'cnv');
                                }}
                            />
                        </Grid>

                        <Grid size={{ xs: 5, sm: 5 }} >
                            <VeriomeTextField
                                fieldErrors={fieldErrors}
                                fieldLabel={findLabel(fieldSchema, 'dbSNPId')}
                                fieldName={'dbSNPId'}
                                currentValue={formFields.dbSNPId ? formFields.dbSNPId : ''}
                                helpText={findHelpText(fieldSchema, 'dbSNPId')}
                                isFocus={false}
                                isRequired={false}
                                isMultiline={false}
                                sxStyle={{
                                    width: 200,
                                    mr: '10px'
                                }}
                                handleBlur={() => {
                                    handleBlur('dbSNPId');
                                }}
                                handleChange={(event) => {
                                    handleChange(event, 'dbSNPId');
                                }}
                            />
                        </Grid>

                    </Grid>

                </Grid >

            </CardContent >

            <CardActions style={{ justifyContent: 'flex-end', marginBottom: '0px' }}>
                {isArrayElement &&
                    <Tooltip title={'Delete this variant'} placement="top">
                        <Button
                            color="error"
                            size="small"
                            onClick={(ev) => {
                                ev.preventDefault();
                                ev.stopPropagation();
                                handleDelete(formFields);
                            }}
                        >
                            Delete
                        </Button>
                    </Tooltip>
                }
            </CardActions>

        </Card >
    );
}

VariantObjField.propTypes = {
    annotationType: PropTypes.string,
    biocurations: PropTypes.object,
    fieldSchema: PropTypes.array.isRequired,
    formFields: PropTypes.object.isRequired,
    isArrayElement: PropTypes.bool,
    handleBlur: PropTypes.func,
    handleChange: PropTypes.func.isRequired,
    handleDelete: PropTypes.func,
    handleLookup: PropTypes.func
};

export default VariantObjField;
